import React, { FC, forwardRef, RefAttributes, useMemo } from 'react';
import { get, map } from 'lodash';
import { NavMenuItem } from '../SideNav';
import { sanitizeDOM } from '@trip-ux/common';
import { useSelector } from 'react-redux';
import { ReduxStore, isReferenceId } from '@trip-ux/redux-store';
import { AUTH_HEADERS } from '@trip-ux/constants';
import { Footer as MuxFooter } from '@manulife/mux';
import { Manulife, ManulifeFr } from '@manulife/mux-logos';

export type FooterVariant = 'PRIMARY' | 'SECONDARY';
export interface FooterProps {
  topNavigationList: NavMenuItem[];
  copyrightText: string;
  FooterText: string;
  className?: string;
}

export const Footer: FC<FooterProps & RefAttributes<HTMLDivElement>> = forwardRef(
  (
    { topNavigationList, copyrightText, FooterText, className = ''},
    ref
  ) => {
    const { config, } = useSelector((store: ReduxStore) => ({
      config: store.config
    }));

    const locale = config?.locale || 'en';

    // Convert topNavigationList to MUX Footer items format
    const items = useMemo(() => {
      return topNavigationList.map((nav) => ({
        id: nav.navId,
        title: nav.label,
        href: nav.href,
        isExternal: nav.target === '_blank'
      }));
    }, [topNavigationList]);

    // Determine which logo to use based on locale
    const logo = useMemo(() => {
      const LogoComponent = locale === 'fr' ? ManulifeFr.REVERSE : Manulife.REVERSE;
      return <LogoComponent style={MuxFooter.DEFAULT_FOOTER_LOGO_STYLE} />;
    }, [locale]);

    // Extract copyright text from HTML and replace year with current year
    const parsedCopyrightText = useMemo(() => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sanitizeDOM(copyrightText);
      return tempDiv.textContent || tempDiv.innerText || '';
    }, [copyrightText]);

    const extractedCopyrightText = parsedCopyrightText.replace(/©\s*\d{4}/, `© ${new Date().getFullYear()}`);

    // Extract legal text from FooterText if available
    const legalText = useMemo(() => {
      if (FooterText) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sanitizeDOM(FooterText);
        return tempDiv.textContent || tempDiv.innerText || '';
      }
      return undefined;
    }, [FooterText]);

    return (
      <MuxFooter
        ref={ref}
        className={className}
        items={items}
        logo={logo}
        copyrightYear=''
        copyrightText={extractedCopyrightText}
        legalText={legalText}
      />
    );
  }
);

Footer.displayName = 'Footer';
export default Footer;
